import React from 'react';
import { Link } from 'react-router-dom';
import MagasinBadge from '../../components/MagasinBadge';
import { fmtEuro, getAllowedStatuses, repairFinancials, repairStatusBadge } from './reparationsUtils';

const ActionBtn = ({ onClick, href, label, testId }) => (
  href ? (
    <Link
      to={href}
      className="min-h-[34px] px-2.5 rounded-lg flex items-center justify-center text-[10px] font-semibold bg-white/10 text-gray-300 hover:bg-white/15"
      data-testid={testId}
    >
      {label}
    </Link>
  ) : (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); onClick?.(); }}
      className="min-h-[34px] px-2.5 rounded-lg text-[10px] font-semibold bg-white/10 text-gray-300 hover:bg-white/15"
      data-testid={testId}
    >
      {label}
    </button>
  )
);

const ReparationsDeviceCard = ({
  device,
  selected,
  checked,
  statuses,
  isAdmin,
  compact,
  onSelect,
  onToggleCheck,
  onStatusClick,
  onAcomptes,
  onAddAcompte,
  onOpenPrint,
  onTimeline,
  onNotes,
  onChat,
}) => {
  const tone = repairStatusBadge(device.status);
  const allowed = getAllowedStatuses(device.status, statuses, isAdmin);
  const { reste } = repairFinancials(device);

  return (
    <article
      role="button"
      tabIndex={0}
      onClick={onSelect}
      onKeyDown={(e) => e.key === 'Enter' && onSelect?.()}
      className={`rounded-xl border p-4 flex flex-col gap-2.5 shrink-0 overflow-hidden w-full min-w-0 cursor-pointer transition ${
        checked
          ? 'border-cyan-500/50 bg-[#152028]'
          : selected
            ? 'border-red-500/50 bg-[#1a1f2a]'
            : 'border-white/10 bg-[#151820] hover:border-white/20'
      }`}
      data-testid={`repair-card-${device.ref}`}
    >
      <div className="flex items-start justify-between gap-2 min-w-0">
        <div className="flex items-center gap-2 min-w-0 flex-wrap">
          <input
            type="checkbox"
            checked={!!checked}
            onChange={() => onToggleCheck?.(device.ref)}
            onClick={(e) => e.stopPropagation()}
            className="w-4 h-4 shrink-0 accent-cyan-500 cursor-pointer"
            aria-label={`Selectionner ${device.ref}`}
            data-testid={`repair-select-${device.ref}`}
          />
          <span className="text-sm font-bold text-red-400 shrink-0">{device.ref}</span>
          {device.magasin_id && <MagasinBadge code={device.magasin_id} size="sm" />}
        </div>
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            if (allowed.length) onStatusClick?.(device);
          }}
          className={`px-2.5 py-1 rounded-full text-[10px] font-bold border shrink-0 max-w-[45%] truncate ${tone.className} ${
            allowed.length ? 'hover:brightness-110' : 'opacity-60 cursor-not-allowed'
          }`}
          disabled={!allowed.length}
          data-testid={`status-btn-${device.ref}`}
          title={tone.label}
        >
          {tone.label}
        </button>
      </div>

      <div className="min-w-0 space-y-0.5">
        <p className="text-sm font-semibold text-white line-clamp-2 leading-snug">{device.model || '—'}</p>
        {!compact && (
          <p className="text-xs text-gray-400 line-clamp-1">
            {device.client_name || '—'}
            {device.client_phone ? ` · ${device.client_phone}` : ''}
          </p>
        )}
        {!compact && device.problem && (
          <p className="text-xs text-gray-500 line-clamp-1">{device.problem}</p>
        )}
      </div>

      <div className="flex items-center justify-between gap-3 pt-2 mt-auto border-t border-white/5 min-w-0">
        <span className="text-[10px] text-gray-500 truncate min-w-0">
          Tech · <span className="text-gray-300">{device.technician_name || '—'}</span>
        </span>
        <span className={`text-sm font-bold tabular-nums shrink-0 whitespace-nowrap ${reste > 0 ? 'text-red-400' : 'text-emerald-400'}`}>
          {reste > 0 ? `${fmtEuro(reste)} €` : 'Soldé'}
        </span>
      </div>

      {!compact && (
        <div className="flex flex-wrap gap-1.5 pt-0.5" onClick={(e) => e.stopPropagation()}>
          <ActionBtn href={`/reparations/${device.ref}`} label="Fiche" testId={`fiche-${device.ref}`} />
          <ActionBtn onClick={() => onOpenPrint?.(device.ref, 'ticket')} label="Impr." testId={`print-repair-receipt-${device.ref}`} />
          <ActionBtn onClick={() => onTimeline?.(device)} label="Suivi" testId={`repair-timeline-btn-${device.ref}`} />
          <ActionBtn onClick={() => onAddAcompte?.(device)} label="Acompte" />
          <ActionBtn onClick={() => onNotes?.(device.ref)} label="Notes" testId={`repair-notes-btn-${device.ref}`} />
          <ActionBtn onClick={() => onChat?.(device)} label="Chat" testId={`repair-chat-btn-${device.ref}`} />
        </div>
      )}
    </article>
  );
};

export default ReparationsDeviceCard;
